import React from "react";
import Modal from "react-modal";
import styled, { css } from "styled-components";

import { sendMedSKUSampleRequest } from "redux/actions/admin";
import { useDispatch } from "react-redux";
import { useLocation } from "react-router-dom";
import { useAuth } from "contexts/AuthContext";

import { MdInsertChart } from "react-icons/md";
import EmblaCarousel from "./EmblaCarousel/EmblaCarousel";

import DataTableImage from "assets/images/PopUpSampleImages/data-table.png";
import PricingImage from "assets/images/PopUpSampleImages/pricing.png";
import TopSellingProductsImage from "assets/images/PopUpSampleImages/top-selling-products.png";

const POPUP_KEYS = {
  VISIT_COUNT: "us_market_report_suite_visit_count",
  SNOOZED_UNTIL: "us_market_report_suite_snoozed_until",
  NEVER_SHOW: "us_market_report_suite_never_show"
} as const;

const marketReportSlides = [
  {
    src: DataTableImage,
    alt: "Data table preview",
  },
  {
    src: PricingImage,
    alt: "Pricing preview",
  },
  {
    src: TopSellingProductsImage,
    alt: "Top selling products preview",
  },
];

const MarketReportPopUp: React.FC = () => {
  const { pathname } = useLocation();
  const dispatch = useDispatch();
  const { currentUser } = useAuth();
  const timer = React.useRef<NodeJS.Timeout | null>(null);

  const [isOpen, setIsOpen] = React.useState(false);
  const [submitted, setSubmitted] = React.useState(false);

  React.useEffect(() => {
    // only show on the us market report suite page
    if (!pathname.includes("/us-market-report-suite")) return;

    // check if user has dismissed the popup forever
    if (localStorage.getItem(POPUP_KEYS.NEVER_SHOW) === "true") return;

    // check if user has dismissed the popup temporarily
    const snoozedUntil = localStorage.getItem(POPUP_KEYS.SNOOZED_UNTIL);
    if (snoozedUntil && Date.now() < parseInt(snoozedUntil, 10)) return;

    // increment visit count
    const visitCount = parseInt(localStorage.getItem(POPUP_KEYS.VISIT_COUNT) || "0", 10) + 1;
    localStorage.setItem(POPUP_KEYS.VISIT_COUNT, visitCount.toString());

    // show popup on 2nd visit
    if (visitCount >= 2) {
      timer.current = setTimeout(() => {
        setIsOpen(true);
      }, 5000); // show after 2 seconds
    }
  }, [pathname]);

  const handleCancel = () => {
    localStorage.setItem(POPUP_KEYS.NEVER_SHOW, "true");
    setIsOpen(false);
  }

  const handleSnooze = () => {
    const oneMonthFromNow = Date.now() + 30 * 24 * 60 * 60 * 1000; // 30 days in milliseconds
    localStorage.setItem(POPUP_KEYS.SNOOZED_UNTIL, oneMonthFromNow.toString());
    setIsOpen(false);
  }

  const handleRequestSample = () => {
    localStorage.setItem(POPUP_KEYS.NEVER_SHOW, "true");
    const reportName: string[] = pathname.split("/").filter(Boolean) || [];
    const slug: string = reportName.find((name) => name.includes("us-market-report-suite")) || "";
    const readable: string = slug.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
    
    const userRequest = {
      email: currentUser.email || "",
      company: currentUser.company || "",
      message: `${currentUser.name || ""} has requested a sample. They clicked the pop-up from ${readable}.`,
      name: currentUser.name || "",
      phone: currentUser.phone || "",
      title: currentUser.title || "",
      category: "MedSKU Sample Request",
      role: currentUser.role || "",
      subject: "[URGENT] MedSKU Sample Request from Pop-Up",
    }
    setSubmitted(true);

    dispatch(sendMedSKUSampleRequest(userRequest));
  }

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={() => setIsOpen(false)}
      style={modalStyles}
      contentLabel="Market Report"
      ariaHideApp={false}
      shouldCloseOnOverlayClick={false}
    >
      <Container>
        <Header>
          <IconShell>
            <MarketIcon size={34} />
          </IconShell>
          <HeaderCopy>
            <Title>See Market Data at SKU levels</Title>
            <Description>
              MedSKU offers brand & SKU levels, market share, units, ASPs and market value.
            </Description>
          </HeaderCopy>
        </Header>

        <Body>
          {!submitted ? (
            <EmblaCarousel slides={marketReportSlides} options={{ loop: true }} />
          ) : (
            <ThankYouCard>
              <ThankYouTitle>Request Sent</ThankYouTitle>
              <ThankYouText>
                We will send an appropriate sample shortly. Thank you for your interest!
              </ThankYouText>
            </ThankYouCard>
          )}
        </Body>

        <Footer>
          {submitted ? (
            <>
              <CloseButton type="button" onClick={() => setIsOpen(false)}>
                Close
              </CloseButton>   
            </>  
          ) : (
            <>
              <DoNotShowThisAgainButton type="button" onClick={handleCancel}>
                Do not show this again
              </DoNotShowThisAgainButton>
              <FooterSpacer />
              <CancelButton type="button" onClick={handleSnooze}>
                Remind me in 1 month
              </CancelButton>
              <RequestButton type="button" onClick={handleRequestSample}>
                Request Sample
              </RequestButton>   
            </>            
          )}
        </Footer>
      </Container>
    </Modal>
  );
};

const PHONE = "@media (max-width: 600px)";
const SHORT = "@media (max-height: 640px)";

const modalStyles: Modal.Styles = {
  overlay: {
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    zIndex: 9999,
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
  },
  content: {
    top: "50%",
    left: "50%",
    right: "auto",
    bottom: "auto",
    transform: "translate(-50%, -50%)",
    padding: 0,
    border: "none",
    borderRadius: "20px",
    width: "920px",
    maxWidth: "94vw",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    boxShadow: "0 28px 80px rgba(17, 24, 39, 0.28)",
    background: "#fff",
  },
};

// Height cap lives here so it can use dvh, which excludes mobile browser toolbars.
const Container = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-height: 0;
  width: 100%;
  max-height: 88vh;
  max-height: 88dvh;

  ${PHONE} {
    max-height: 92vh;
    max-height: 92dvh;
  }
`;

const Header = styled.div`
  display: flex;
  align-items: flex-start;
  flex-shrink: 0;
  gap: 24px;
  padding: 28px 32px 24px;
  border-bottom: 1px solid rgba(29, 131, 183, 0.12);
  background: linear-gradient(180deg, #f8fcff 0%, #ffffff 100%);
  width: 100%;

  ${SHORT} {
    align-items: center;
    gap: 16px;
    padding: 16px 32px;
  }

  ${PHONE} {
    gap: 14px;
    padding: 18px 16px 16px;
  }
`;

const MarketIcon = styled(MdInsertChart)`
  color: ${(props) => props.theme.colors.idataBlue};
`;

const IconShell = styled.div`
  width: 64px;
  height: 64px;
  border-radius: 18px;
  display: grid;
  place-items: center;
  flex: 0 0 auto;
  background: linear-gradient(135deg, rgba(29, 131, 183, 0.12), rgba(29, 131, 183, 0.04));
  border: 1px solid rgba(29, 131, 183, 0.14);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.7);

  ${SHORT} {
    width: 48px;
    height: 48px;
    border-radius: 14px;
  }

  ${PHONE} {
    width: 44px;
    height: 44px;
    border-radius: 12px;

    svg {
      width: 26px;
      height: 26px;
    }
  }
`;

const HeaderCopy = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
  min-width: 0;
  padding-top: 2px;

  ${SHORT} {
    gap: 4px;
    padding-top: 0;
  }
`;

const Title = styled.h2`
  margin: 0;
  font-size: 2.8rem;
  line-height: 1.15;
  letter-spacing: -0.02em;
  color: #16324a;

  ${SHORT} {
    font-size: 2.4rem;
  }

  ${PHONE} {
    font-size: 2.1rem;
  }
`;

const Description = styled.p`
  margin: 0;
  font-size: 1.65rem;
  line-height: 1.6;
  color: #607080;

  ${SHORT} {
    font-size: 1.5rem;
    line-height: 1.4;
  }

  ${PHONE} {
    font-size: 1.4rem;
    line-height: 1.45;
  }
`;

const Body = styled.div`
  flex: 1 1 auto;
  min-height: 0;
  padding: 28px 32px;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  background: linear-gradient(180deg, #ffffff 0%, #fbfdff 100%);

  ${SHORT} {
    padding: 16px 32px;
  }

  ${PHONE} {
    padding: 16px;
  }
`;

const Footer = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  align-items: center;
  flex-shrink: 0;
  gap: 12px;
  padding: 20px 32px 28px;
  border-top: 1px solid rgba(29, 131, 183, 0.12);
  background: #fff;

  ${SHORT} {
    padding: 12px 32px 16px;
  }

  /* stack buttons full width, primary action first */
  ${PHONE} {
    flex-direction: column;
    align-items: stretch;
    gap: 8px;
    padding: 12px 16px 16px;
  }
`;

const ThankYouCard = styled.div`
  border-radius: 18px;
  padding: 28px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  align-items: center;
  justify-content: center;
  background: linear-gradient(180deg, rgba(29, 131, 183, 0.03), rgba(29, 131, 183, 0.01));
  border: 1px solid rgba(29, 131, 183, 0.08);

  ${PHONE} {
    padding: 20px 16px;
  }
`;

const ThankYouTitle = styled.h3`
  margin: 0;
  font-size: 2.3rem;
  color: #16324a;
  text-align: center;

  ${PHONE} {
    font-size: 2rem;
  }
`;

const ThankYouText = styled.p`
  margin: 0;
  font-size: 1.6rem;
  color: #607080;
  text-align: center;

  ${PHONE} {
    font-size: 1.45rem;
  }
`;

const FooterSpacer = styled.div`
  flex: 1;

  ${PHONE} {
    display: none;
  }
`;

const buttonBase = css`
  min-width: 11rem;
  padding: 1.45rem 1.95rem;
  border-radius: 999px;
  font-size: 1.75rem;
  font-weight: 600;
  white-space: nowrap;

  ${SHORT} {
    padding: 1.1rem 1.7rem;
    font-size: 1.6rem;
  }

  ${PHONE} {
    width: 100%;
    padding: 1.2rem 1.6rem;
    font-size: 1.6rem;
    white-space: normal;
  }
`;

const DoNotShowThisAgainButton = styled.button`
  ${buttonBase}
  color: ${(props) => props.theme.colors.idataDarkGrey};

  &:hover {
    text-decoration: underline;
  }

  ${PHONE} {
    order: 3;
    padding-top: 0.6rem;
    padding-bottom: 0.6rem;
  }
`;

const CancelButton = styled.button`
  ${buttonBase}
  background: #E5E7EB;
  color: ${(props) => props.theme.colors.idataDarkGrey};

  &:hover {
    background: #D1D5DB;
  }

  ${PHONE} {
    order: 2;
  }
`;

const RequestButton = styled.button`
  ${buttonBase}
  background: ${(props) => props.theme.colors.idataBlue};
  color: white;

  &:hover {
    background: ${(props) => props.theme.colors.idataDarkBlue};
  }

  ${PHONE} {
    order: 1;
  }
`;

const CloseButton = styled.button`
  ${buttonBase}
  background: ${(props) => props.theme.colors.idataBlue};
  color: white;

  &:hover {
    background: ${(props) => props.theme.colors.idataDarkBlue};
  }
`;

export default MarketReportPopUp;